<template>
  <NuxtLayout :name="layoutName">
    <template #main>
      <section
        class="flex flex-col items-center justify-center w-full min-h-[70vh] px-4 py-12 sm:py-16  text-center"
      >
        <img
          :src="errorImage"
          alt="Disconnected server illustration"
          class="w-[240px] sm:w-[320px] md:w-[384px] h-auto md:h-[282px] object-contain mb-8"
        />
        <h1 class="font-medium text-[28px] leading-[28px] text-[#1F1F1F]">
          Oh ho!
          <span class="font-bold text-[40px] leading-[48px] text-black">{{ codeLabel }}</span>
          {{ headlineSuffix }}
        </h1>
        <p class="mt-4 text-[#4B5563] text-[16px] leading-[100%] max-w-[420px]">
          {{ description }}
        </p>
        <button
          type="button"
          :class="['mt-8 w-full max-w-[384px] h-10 rounded-lg text-white text-sm font-medium leading-5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors', isVersePartner ? 'bg-[#CA5096] hover:bg-[#b84485] text-white'
            : 'bg-primary text-white']"
          @click="handleGoHome"
        >
          Go Home
        </button>
      </section>
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import type { NuxtError } from '#app'
import { GoogleAnalytics } from "~/google_analytics/events";
import { EventParameter, EventParameterValue } from "~/google_analytics/events.enum";

const props = defineProps<{
  error: NuxtError
}>()

const runtimeConfig = useRuntimeConfig()
const layoutName = computed(() => runtimeConfig.public.templateVersion || 'v2')

const statusCode = computed(() => props.error?.statusCode ?? 500)
const isClientError = computed(() => statusCode.value >= 400 && statusCode.value < 500)
const isServerError = computed(() => statusCode.value >= 500)

const codeLabel = computed(() => (isServerError.value ? statusCode.value : statusCode.value))

const headlineSuffix = computed(() => {
  if (isClientError.value) return ' page not found!'
  if (isServerError.value) return ' Internal Server Error'
  return ' something went wrong!'
})

const description = computed(() =>
  isClientError.value
    ? "The page you are looking for doesn't exist or another error occurred."
    : 'Sorry, something went wrong. Please try again later.'
)

const isVersePartner = computed(() =>
  runtimeConfig?.public?.partnerCode?.toLowerCase() === 'oneindiamall'
)

const errorImage = computed(() => {
  return isVersePartner.value
    ? '/v1/icons/Error_emptystate_v1.png'
    : 'https://cdn.nlincs.io/mall4all/1765872788081.webp'
})


const route = useRoute()
route.meta.hideFooter = true
const handleGoHome = () => {
  navigateTo('/')
}

const trackError = () => {
  GoogleAnalytics.cta({
    [EventParameter.CTA_NAME]: `error_${statusCode.value}`,
    [EventParameter.ERROR_MESSAGE]: headlineSuffix.value,
  })
}
onMounted(() => {
  trackError()
})
</script>
